import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import CardBulletPoint from "@/components/shared/futureChat/cards/CardBulletPoint";
import IconCancel from "@/components/shared/icons/Cancel";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import { cn } from "@/lib/utils";
import { inject, observer } from "mobx-react";
import ExportedImage from "next-image-export-optimizer";
import Link from "next/link";
import { useContext, useEffect, useState } from "react";
import store, { Store } from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";

type AgeProps = {
    onLetsGo: () => void;
    onCancel: () => void;
    handleOver50: (active: boolean) => void;
    onLetsGo50: () => void;
    store?: Store;
    className?: string;
};

const Age: React.FC<AgeProps> = ({ handleOver50, onLetsGo, onLetsGo50, onCancel }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { ageLocalized, globalLocalized, chatbotLocalized, isCameraAllowed } = store;

    const [windowWidth, setWindowWidth] = useState(0);

    useEffect(() => {
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getImageSize = () => {
        if (windowWidth < 480) {
            return chatbotLocalized?.keyvisualDark320px;
        } else if (windowWidth < 768) {
            return chatbotLocalized?.keyvisualDark480px;
        } else if (windowWidth < 1024) {
            return chatbotLocalized?.keyvisualDark768px;
        } else if (windowWidth < 1280) {
            return chatbotLocalized?.keyvisualDark1024px;
        } else if (windowWidth < 1920) {
            return chatbotLocalized?.keyvisualDark1280px;
        } else if (windowWidth >= 1920) {
            return chatbotLocalized?.keyvisualDark1920px;
        }
    };

    const background = getImageSize();

    const { trackView, screenView } = useContext(TrackingContext);
    useEffect(() => {
        screenView("1.2 - Age Check");
    }, []);

    const [cameraPermission, setCameraPermission] = useState<boolean>(false);

    async function checkCameraPermission() {
        try {
            if (navigator.permissions) {
                const permissionStatus = await navigator.permissions.query({ name: "camera" as PermissionName });
                let flagToReload = false;
                permissionStatus.onchange = () => {
                    if (permissionStatus.state === "granted") {
                        flagToReload = true;
                    } else {
                        flagToReload = false;
                    }
                    updateCameraPermissionState(permissionStatus, flagToReload);
                };
                updateCameraPermissionState(permissionStatus, flagToReload);
                return permissionStatus.state;
            } else {
                console.log("Permissions API is not supported");
            }
        } catch (error) {
            console.error("CheckCameraPermissionError: ", error);
        }
    }

    function updateCameraPermissionState(permissionStatus: any, flagToReload: boolean) {
        if (permissionStatus.state === "granted") {
            setCameraPermission(true);
            if (flagToReload) {
                window.location.reload();
            }
        } else if (permissionStatus.state === "denied") {
            setCameraPermission(false);
        } else {
            setCameraPermission(true);
            console.log("Camera Access is pending");
        }
    }

    useEffect(() => {
        checkCameraPermission();
    }, []);

    function detectEnvironment() {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        const isSafari = /safari/i.test(userAgent) && !/chrome/i.test(userAgent);

        if (isSafari) {
            if (/iPhone|iPad|iPod|iOS/.test(platform)) {
                return "iOS";
            } else if (/Mac/.test(platform)) {
                return "macOS";
            }
        }
        return "notSafari";
    }

    const environment = detectEnvironment();
    let instructions;

    let useCameraPermissions;

    switch (environment) {
        case "iOS":
            instructions = ageLocalized?.chatbotCameraPermissionInstructionsiOS;
            useCameraPermissions = isCameraAllowed;
            break;
        case "macOS":
            instructions = ageLocalized?.chatbotCameraPermissionInstructionsMacOS;
            useCameraPermissions = isCameraAllowed;
            break;
        default:
            instructions = ageLocalized?.chatbotCameraPermissionInstructionsChrome;
            useCameraPermissions = isCameraAllowed ? isCameraAllowed : cameraPermission;
    }

    return (
        <div className="Age | flex h-full w-full flex-col items-center">
            {background !== undefined && background !== null && (
                <div className={cn("absolute h-full w-full")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(background)}
                        alt={getAltTextFromGeneralImage(background)}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
            )}
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div className="relative h-[158px] w-[247px]">
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="cover"
                        objectPosition="center"
                    />
                </div>
            )}

            <div
                className={cn(
                    "absolute bottom-[46px] flex w-full flex-col gap-[22px] px-[16px]",
                    "2xs:bottom-[68px] 2xs:w-[364px] 2xs:px-0",
                    "z-[100]",
                )}
            >
                {useCameraPermissions ? (
                    <>
                        <Card>
                            <div className="font-frutigerBold text-[12px] font-bold leading-[125%] tracking-minus-2 text-white 2xs:text-[16px]">
                                {ageLocalized?.cardTitle}
                            </div>
                            <div className="flex flex-wrap items-center justify-center gap-[10px]">
                                <ButtonChat
                                    className="w-max"
                                    variant="onCard"
                                    onClick={() => {
                                        handleOver50(false);
                                        onLetsGo();
                                        trackView("age1");
                                    }}
                                >
                                    {ageLocalized?.selectButtonOne.text}
                                </ButtonChat>
                                <ButtonChat
                                    className="w-max"
                                    variant="onCard"
                                    onClick={() => {
                                        handleOver50(false);
                                        onLetsGo();
                                        trackView("age2");
                                    }}
                                >
                                    {ageLocalized?.selectButtonTwo.text}
                                </ButtonChat>
                                <ButtonChat
                                    className="w-max"
                                    variant="onCard"
                                    onClick={() => {
                                        handleOver50(true);
                                        onLetsGo50();
                                        trackView("age3");
                                    }}
                                >
                                    {ageLocalized?.selectButtonThree.text}
                                </ButtonChat>
                            </div>
                        </Card>
                        <ButtonChat
                            onClick={onCancel}
                            variant="secondary"
                            iconRight={
                                <ExportedImage
                                    src={
                                        ageLocalized?.button.icon !== undefined && ageLocalized?.button.icon !== null
                                            ? getImageUrlFromGeneralImage(ageLocalized.button.icon)
                                            : "No image provided"
                                    }
                                    alt={
                                        ageLocalized?.button.icon !== undefined && ageLocalized?.button.icon !== null
                                            ? getAltTextFromGeneralImage(ageLocalized.button.icon)
                                            : "No image provided"
                                    }
                                    layout="fill"
                                />
                            }
                        >
                            {ageLocalized?.button.text}
                        </ButtonChat>
                    </>
                ) : (
                    <>
                        <Card>
                            <div className="font-frutigerBold text-[12px] font-bold leading-[125%] tracking-minus-2 text-white 2xs:text-[16px]">
                                {ageLocalized?.chatbotCameraDeniedInfo}
                            </div>
                            {instructions &&
                                instructions.map(
                                    (bulletpoint, bulletpointIndex) =>
                                        bulletpoint?.icon !== undefined &&
                                        bulletpoint?.icon !== null &&
                                        bulletpoint.text !== undefined && (
                                            <CardBulletPoint
                                                key={bulletpointIndex}
                                                icon={
                                                    <ExportedImage
                                                        src={getImageUrlFromGeneralImage(bulletpoint?.icon)}
                                                        alt={getAltTextFromGeneralImage(bulletpoint?.icon)}
                                                        layout="fill"
                                                        objectFit="cover"
                                                    />
                                                }
                                            >
                                                {bulletpoint?.text}
                                            </CardBulletPoint>
                                        ),
                                )}
                        </Card>
                        <ButtonChat
                            onClick={() => window.location.reload()}
                            iconRight={
                                <ExportedImage
                                    src={
                                        ageLocalized?.chatbotCameraPermissionsDeniedButton.icon !== undefined &&
                                        ageLocalized?.chatbotCameraPermissionsDeniedButton.icon !== null
                                            ? getImageUrlFromGeneralImage(
                                                  ageLocalized.chatbotCameraPermissionsDeniedButton.icon,
                                              )
                                            : "No image provided"
                                    }
                                    alt={
                                        ageLocalized?.chatbotCameraPermissionsDeniedButton.icon !== undefined &&
                                        ageLocalized?.chatbotCameraPermissionsDeniedButton.icon !== null
                                            ? getAltTextFromGeneralImage(
                                                  ageLocalized.chatbotCameraPermissionsDeniedButton.icon,
                                              )
                                            : "No image provided"
                                    }
                                    layout="fill"
                                />
                            }
                        >
                            {ageLocalized?.chatbotCameraPermissionsDeniedButton.text}
                        </ButtonChat>
                    </>
                )}
            </div>
        </div>
    );
};

export default inject("store")(observer(Age));
